import React, { useState, useEffect } from "react";
import _ from "lodash";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Box, useTheme, useMediaQuery } from "@mui/material";
import Header from "../common/components/Header/HeaderNew";
import Footer from "../common/components/Footer/Footer";
import ScrollToTop from "../common/components/scroll-to-top/scroll-to-top";
import AdminMainLayout from "../features/admin-section/admin-main-layout";
import ProviderMainLayout from "../features/provider-section/provider-main-layout";
import PreneedMainLayout from "../features/preneed-section/preneed-main-layout"; //  added
import { useSelector, useDispatch } from "react-redux";
import { routes } from "../constants/routes";
import { updateLastActivity } from "../common/session/sessionTimer";

const getUserTypeFromPath = (path: string) => {
  if (path.startsWith("/admin")) return "superAdmin";
  if (path.startsWith("/provider")) return "provider";
  if (path.startsWith("/preneed")) return "preNeed"; //  added
  return "user";
};

const getActiveUserTypeFromDetails = (path: string) => {
  if (path.startsWith("/admin")) return "superAdmin";
  if (path.startsWith("/provider")) return "provider";
  if (path.startsWith("/preneed")) return "preNeed";
  return "user";
};


const MainLayout: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const path = location.pathname;

  // Redux values
  const adminDetails = useSelector((state: any) => state?.session?.superAdminDetails);
  const providerDetails = useSelector((state: any) => state?.session?.providerDetails);
  const preNeedDetails = useSelector((state: any) => state?.session?.preNeedDetails); //  added
  const userDetails = useSelector((state: any) => state?.session?.userDetails);

  const autoLogoutOfSuperAdmin = useSelector(
    (state: any) => state?.session?.autoLogoutOfSuperAdmin
  );
  const autoLogoutOfUser = useSelector((state: any) => state?.session?.autoLogoutOfUser);
  const autoLogoutOfProvider = useSelector(
    (state: any) => state?.session?.autoLogoutOfProvider
  );
  const autoLogoutOfPreNeed = useSelector(
    (state: any) => state?.session?.autoLogoutOfPreNeed //  added
  );

  const [isAdmin, setIsAdmin] = useState(false);
  const [isProvider, setIsProvider] = useState(false);
  const [isPreNeed, setIsPreNeed] = useState(false); //  added

  // Pages to always show header/footer on
  const mainPages = ["/", "/home", "/contact-us", "/services", "/become-provider", "/terms-of-service"];

  // Pages to hide header/footer on mobile
  const hideOnMobilePages = ["/home/list-of-funeral-homes"];

  // Admin + Provider + Preneed prefixes
  const adminPages = ["/admin/"];
  const providerPages = ["/provider/"];
  const preNeedPages = ["/preneed/"]; //  added

  const isAdminPage = adminPages.some((p) => path.startsWith(p));
  const isProviderPage = providerPages.some((p) => path.startsWith(p));
  const isPreNeedPage = preNeedPages.some((p) => path.startsWith(p)); //  added

  const shouldShowHeaderFooter = () => {
    if (!isMobile && !isAdminPage && !isProviderPage && !isPreNeedPage) return true; //  updated
    if (mainPages.includes(path)) return true;
    if (hideOnMobilePages.some((p) => path.startsWith(p))) return false;
    if (isAdminPage || isProviderPage || isPreNeedPage) return false; //  updated
    return true;
  };

  useEffect(() => {
    setIsAdmin(!_.isEmpty(adminDetails));
  }, [adminDetails]);

  useEffect(() => {
    setIsProvider(!_.isEmpty(providerDetails));
  }, [providerDetails]);

  useEffect(() => {
    setIsPreNeed(!_.isEmpty(preNeedDetails)); //  added
  }, [preNeedDetails]);

  useEffect(() => {
    // Only trigger auto-logout if it matches the current application role
    const appType = getUserTypeFromPath(path);

    if (autoLogoutOfUser && appType === "user") {
      navigate(routes.LOGIN);
      dispatch({
        type: "SESSION:SET_AUTO_LOGOUT",
        payload: { userType: "user", autoLogoutOfUser: false },
      });
    }

    if (autoLogoutOfSuperAdmin && appType === "superAdmin") {
      navigate(routes.ADMIN_LOGIN, { replace: true, state: {} });
      dispatch({
        type: "SESSION:SET_AUTO_LOGOUT",
        payload: { userType: "superAdmin", autoLogoutOfSuperAdmin: false },
      });
    }

    if (autoLogoutOfProvider && appType === "provider") {
      navigate(routes.PROVIDER_LOGIN, { replace: true, state: {} });
      dispatch({
        type: "SESSION:SET_AUTO_LOGOUT",
        payload: { userType: "provider", autoLogoutOfProvider: false },
      });
    }

    if (autoLogoutOfPreNeed && appType === "preNeed") {
      navigate(routes.PRENEED_LOGIN, { replace: true, state: {} });
      dispatch({
        type: "SESSION:SET_AUTO_LOGOUT",
        payload: { userType: "preNeed", autoLogoutOfPreNeed: false },
      });
    }
  }, [
    path, // added path dependency to ensure check happens on navigation too if needed
    autoLogoutOfSuperAdmin,
    autoLogoutOfUser,
    autoLogoutOfProvider,
    autoLogoutOfPreNeed,
    navigate,
    dispatch,
  ]);

useEffect(() => {
  const activeUserType = getActiveUserTypeFromDetails(path);

  if (
    (activeUserType === "superAdmin" && !_.isEmpty(adminDetails)) ||
    (activeUserType === "provider" && !_.isEmpty(providerDetails)) ||
    (activeUserType === "preNeed" && !_.isEmpty(preNeedDetails)) ||
    (activeUserType === "user" && !_.isEmpty(userDetails))
  ) {
    updateLastActivity(activeUserType);
  }
}, [path, adminDetails, providerDetails, preNeedDetails, userDetails]);


  const isGrayBackgroundPage = path.startsWith("/home/list-of-funeral-homes");

  return (
    <Box
      className={`main-layout ${isAdminPage ? "admin-layout" : ""} ${isProviderPage ? "provider-layout" : ""
        } ${isPreNeedPage ? "preneed-layout" : ""}`} //  added
      style={
        !isAdminPage && !isProviderPage && !isPreNeedPage
          ? { minHeight: "100vh", display: "flex", flexDirection: "column", backgroundColor: isGrayBackgroundPage ? "#e8e8e8" : "transparent" }
          : {}
      }
    >
      <ScrollToTop />

      {shouldShowHeaderFooter() && <Header />}

      {/* Sidebars */}
      {isAdminPage && isAdmin && <AdminMainLayout />}
      {isProviderPage && isProvider && <ProviderMainLayout />}
      {isPreNeedPage && isPreNeed && <PreneedMainLayout />} {/*  added */}

      <Box
        component="main"
        flex={1}
        sx={{
          width:
            isAdminPage || isProviderPage || isPreNeedPage ? "auto" : "100%",
          minWidth: 0,
        }}
        className={`${isAdminPage || isProviderPage || isPreNeedPage ? "content" : ""}`} //  updated
      >
        <Outlet />
      </Box>

      {shouldShowHeaderFooter() && <Footer />}
    </Box>
  );
};

export default MainLayout;
